import { useState } from 'react';
import { Chip, IconButton, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';
import { MainContentPanel } from '../components/MainContentPanel';
import Loading from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatDate } from '../Formatters';
import { GET_SUBSCRIPTIONS } from '../discounts/discount.graphql';
import { DELETE_USAGE_LIMIT, GET_USAGE_LIMITS } from './usagelimits.graphql';
import { RuleDialog, scopeLabel } from './RuleDialog';
import { productName } from './products';
import { UsageLimitRule } from './interfaces';

export function UsageLimitsOverview() {
  const queryTask = useQuery<{ usageLimits: UsageLimitRule[] }>(GET_USAGE_LIMITS);
  const subscriptionsQuery = useQuery<{ subscriptions: { id: number; name: string }[] }>(GET_SUBSCRIPTIONS);
  const [dialog, setDialog] = useState<'closed' | 'new' | UsageLimitRule>('closed');
  const [toDelete, setToDelete] = useState<UsageLimitRule | null>(null);

  const [deleteRule] = useMutation<any, any>(DELETE_USAGE_LIMIT, { refetchQueries: [{ query: GET_USAGE_LIMITS }] });

  const subscriptionNames = new Map((subscriptionsQuery.data?.subscriptions ?? []).map((s) => [s.id, s.name]));

  return (
    <MainContentPanel>
      <Typography gutterBottom variant="h5">
        <span>Bruksgrenser</span>
        <IconButton size="large" onClick={() => setDialog('new')}>
          <Add />
        </IconButton>
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Grenser for antall anvisninger og KI-tokens per bruker i en rullerende periode. Regel for bruker overstyrer lisens, som overstyrer abonnement.
        Endringer trer i kraft i portalen innen et par minutter.
      </Typography>

      {queryTask.loading && <Loading />}
      {queryTask.error && <Typography>Feil! {queryTask.error.message}</Typography>}
      {queryTask.data && (
        <RuleTable
          rules={queryTask.data.usageLimits}
          subscriptionNames={subscriptionNames}
          onEdit={(rule) => setDialog(rule)}
          onDelete={(rule) => setToDelete(rule)}
        />
      )}

      {dialog !== 'closed' && <RuleDialog open onClose={() => setDialog('closed')} rule={dialog === 'new' ? null : dialog} />}

      <ConfirmDialog
        open={!!toDelete}
        title="Slett bruksgrense"
        message={`Er du sikker på at du vil slette regelen for ${toDelete ? scopeLabel(toDelete) : ''}?`}
        confirmText="Slett"
        confirm={async () => {
          await deleteRule({ variables: { input: { id: toDelete!.id } } });
          setToDelete(null);
        }}
        deny={() => setToDelete(null)}
      />
    </MainContentPanel>
  );
}

/** The filterable rule scope: mirrors the resolver's precedence categories. */
function ruleType(rule: UsageLimitRule): string {
  if (rule.userId) return 'Bruker';
  if (rule.licenseId) return 'Lisens';
  if ((rule.subscriptionIds ?? []).length > 0) return 'Abonnement';
  return 'Global';
}

function RuleTable({
  rules,
  subscriptionNames,
  onEdit,
  onDelete,
}: {
  rules: UsageLimitRule[];
  subscriptionNames: Map<number, string>;
  onEdit: (rule: UsageLimitRule) => void;
  onDelete: (rule: UsageLimitRule) => void;
}) {
  const limitText = (max?: number | null, windowDays?: number | null) => (max != null ? `${max.toLocaleString('nb-NO')} / ${windowDays} dager` : '—');

  const columns: GridColDef<UsageLimitRule>[] = [
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 130,
      type: 'singleSelect',
      valueOptions: ['Global', 'Abonnement', 'Lisens', 'Bruker'],
      valueGetter: (_, row) => ruleType(row),
    },
    {
      field: 'sourceId',
      headerName: 'Produkt',
      minWidth: 140,
      flex: 1,
      type: 'singleSelect',
      valueOptions: ['Byggforskserien', 'Våtromsnormen', 'Begge (eldre regel)'],
      valueGetter: (_, row) => productName(row.sourceId),
    },
    {
      field: 'scope',
      headerName: 'Gjelder',
      minWidth: 200,
      flex: 1.5,
      valueGetter: (_, row) => scopeLabel(row, subscriptionNames),
      renderCell: (params) => {
        const to = params.row.userId ? `/user/${params.row.userId}?tab=bruk` : params.row.licenseId ? `/license/${params.row.licenseId}` : null;
        return to ? <Link to={to}>{params.value}</Link> : <span>{params.value}</span>;
      },
    },
    {
      field: 'maxDocuments',
      headerName: 'Anvisninger',
      minWidth: 140,
      flex: 1,
      valueGetter: (_, row) => limitText(row.maxDocuments, row.documentWindowDays),
    },
    {
      field: 'maxAiTokens',
      headerName: 'KI-tokens',
      minWidth: 140,
      flex: 1,
      valueGetter: (_, row) => limitText(row.maxAiTokens, row.aiTokenWindowDays),
    },
    {
      field: 'active',
      headerName: 'Status',
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: ['Aktiv', 'Inaktiv'],
      valueGetter: (_, row) => (row.active ? 'Aktiv' : 'Inaktiv'),
      renderCell: (params) => <Chip size="small" label={params.row.active ? 'Aktiv' : 'Inaktiv'} color={params.row.active ? 'success' : 'default'} />,
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Sist endret',
      minWidth: 150,
      flex: 1,
      type: 'date',
      valueGetter: (_, row) => (row.lastUpdatedAt ? new Date(row.lastUpdatedAt) : null),
      renderCell: (params) => <span>{formatDate(params.row.lastUpdatedAt)}</span>,
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <IconButton size="small" title="Endre" onClick={() => onEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Slett" onClick={() => onDelete(params.row)}>
            <Delete fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <DataGridPro
      autoHeight
      density="compact"
      showToolbar
      rows={rules}
      columns={columns}
      getRowId={(row) => row.id}
      localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
      pagination
      pageSizeOptions={[25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 50,
            page: 0,
          },
        },
      }}
      sx={{
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
      }}
    />
  );
}
